//
// # Base Context Menu
//
// Abstract base class providing shared infrastructure for all context menus.
// Handles DOM element lifecycle, styling, positioning, theme updates, and
// global click-to-dismiss behavior.
//

import type { Editor } from "./editor";
import { Palette } from "DrawCore/src/style";

// Subclass-specific style dimensions.
export interface ContextMenuStyleConfig {
  readonly minWidth: string;
  readonly maxWidth: string;
  readonly overflow?: string;
}

// Escape a string for safe interpolation into HTML text content and attributes.
const htmlEscapeMap: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#039;",
};

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (ch) => htmlEscapeMap[ch]);
}

export abstract class BaseContextMenu {
  protected menuElement: HTMLDivElement | null = null;

  // Style sheet element for dynamic theme updates
  protected styleSheet: HTMLStyleElement | null = null;

  // Bound reference to the global click handler so it can be removed on dispose
  private readonly globalClickHandler: (e: MouseEvent) => void;

  constructor(protected readonly editor: Editor) {
    this.globalClickHandler = (e: MouseEvent) => {
      if (this.isVisible() && !this.menuElement!.contains(e.target as Node)) {
        this.hide();
      }
    };
    this.setupGlobalClickHandler();
  }

  // -- Abstract hooks for subclasses --

  // Dimensions for the menu element.
  abstract readonly styleConfig: ContextMenuStyleConfig;

  // Additional CSS rules specific to the subclass (appended after common rules).
  protected abstract getSubclassStyleSheet(): string;

  // Reset subclass-specific state (called by hide and dispose).
  protected abstract clearState(): void;

  // -- Concrete shared methods --

  // Whether the context menu is currently visible.
  isVisible = (): boolean => {
    return (
      this.menuElement !== null && this.menuElement.style.display !== "none"
    );
  };

  // Hide the context menu
  hide = (): void => {
    if (this.menuElement) {
      this.menuElement.style.display = "none";
    }
    this.clearState();
  };

  // Update styles when theme changes (call from editor's theme change handler)
  updateTheme = (): void => {
    this.applyMenuStyles();
    this.updateStyleSheet();
  };

  // Clean up resources
  dispose = (): void => {
    if (this.menuElement) {
      this.menuElement.remove();
      this.menuElement = null;
    }
    if (this.styleSheet) {
      this.styleSheet.remove();
      this.styleSheet = null;
    }
    this.removeGlobalClickHandler();
    this.clearState();
  };

  // -- Helpers for subclass show() methods --

  // Lazily create the menu DOM element and attach styles.
  protected ensureMenuElement = (): void => {
    if (!this.menuElement) {
      this.menuElement = document.createElement("div");
      this.setupMenuStyles();
      // Append directly to body instead of using overlays to avoid positioning issues
      document.body.appendChild(this.menuElement);
    }
  };

  // Set the menu content, make it visible, and position it.
  protected displayMenu = (
    content: string,
    pageX: number,
    pageY: number
  ): void => {
    if (!this.menuElement) return;
    this.menuElement.innerHTML = content;
    this.menuElement.style.display = "block";
    // Position AFTER setting display to ensure overlay's default positioning is overridden
    this.positionMenu(pageX, pageY);
  };

  // -- Private shared methods --

  // Position the menu at the given page coordinates
  private positionMenu = (pageX: number, pageY: number): void => {
    if (!this.menuElement) return;

    const offset = 2;

    this.menuElement.style.left = `${pageX + offset}px`;
    this.menuElement.style.top = `${pageY + offset}px`;

    // Ensure menu doesn't go off-screen
    const rect = this.menuElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Adjust horizontal position if menu would go off right edge
    if (rect.right > viewportWidth) {
      const newLeft = pageX - rect.width - offset;
      // Make sure we don't go off the left edge
      this.menuElement.style.left = `${Math.max(10, newLeft)}px`;
    }

    // Adjust vertical position if menu would go off bottom edge
    if (rect.bottom > viewportHeight) {
      const newTop = pageY - rect.height - offset;
      // Make sure we don't go off the top edge
      this.menuElement.style.top = `${Math.max(10, newTop)}px`;
    }
  };

  // Set up global click handler to close menu when clicking outside
  private setupGlobalClickHandler = (): void => {
    document.addEventListener("mousedown", this.globalClickHandler);
  };

  // Remove the global click handler
  private removeGlobalClickHandler = (): void => {
    document.removeEventListener("mousedown", this.globalClickHandler);
  };

  // Set up styles for the context menu
  private setupMenuStyles = (): void => {
    if (!this.menuElement) return;

    // Apply theme-aware styles
    this.applyMenuStyles();

    // Add CSS classes for internal styling (will be updated on theme change)
    this.styleSheet = document.createElement("style");
    this.updateStyleSheet();
    document.head.appendChild(this.styleSheet);
  };

  // Apply inline styles to menu element
  private applyMenuStyles = (): void => {
    if (!this.menuElement) return;

    const p = Palette.getAll();
    const style = this.menuElement.style;
    style.position = "absolute";
    style.backgroundColor = p.background;
    style.color = p.text;
    style.border = `1px solid ${p.border}`;
    style.borderRadius = "4px";
    style.padding = "0";
    style.fontSize = "12px";
    style.fontFamily = "Monaco, 'Courier New', monospace";
    style.minWidth = this.styleConfig.minWidth;
    style.maxWidth = this.styleConfig.maxWidth;
    if (this.styleConfig.overflow) {
      style.overflow = this.styleConfig.overflow;
    }
    style.zIndex = "10000";
    style.boxShadow = p.boxShadow;
    style.display = "none";
  };

  // Update the stylesheet with current theme colors
  private updateStyleSheet = (): void => {
    if (!this.styleSheet) return;

    const p = Palette.getAll();
    // Common CSS rules shared by all context menus
    let css = `
      .context-menu-info {
        padding: 8px 12px;
      }
      .context-menu-separator {
        height: 1px;
        background-color: ${p.border};
        margin: 4px 0;
      }
      .context-menu-actions {
        padding: 4px 0;
      }
      .context-menu-item {
        padding: 6px 12px;
        cursor: pointer;
        user-select: none;
      }
      .context-menu-item:hover {
        background-color: ${p.backgroundHover};
      }
      .context-menu-item-disabled {
        opacity: 0.4;
        cursor: default;
        pointer-events: none;
      }
    `;

    // Append subclass-specific CSS rules
    css += this.getSubclassStyleSheet();

    this.styleSheet.textContent = css;
  };
}
